import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { PictureAsPdfOutlined, DeleteOutlined } from '@mui/icons-material'

import { Panel } from 'components/Panel'
import Spinner from 'components/Spinner'
import Table from 'components/Table'

import { shortDateTime } from 'helpers/formatters'
import Apptentive from 'helpers/apptentive'
import GA4 from 'helpers/ga4'

import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'
import { useStoreContext } from 'hooks/StoreContext'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import DeleteReportModal from 'pages/Device/DeleteReportModal'

const SystemReportsPanel = () => {
  const { systemReports, areSystemReportsLoaded } = useStoreContext()
  const { hasAccess, showUpgrade, inFreeTrial, hoverTextMessage, LockIcon } =
    usePremiumFeaturesContext()
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [reportToDelete, setReportToDelete] = useState<null | {
    deviceId: string
    s3FileName: string
  }>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [localReports, setLocalReports] = useState(systemReports || [])
  const [isTableUpdating, setIsTableUpdating] = useState(false)

  const onClickReport = () => {
    GA4.sendEvent({
      category: 'Customer Detail Page',
      action: 'Click PDF Report',
      label: 'Report Type: System'
    })

    Apptentive.sendEvent('system_pdf_report_viewed')
  }

  const SortByTimeStamp = (reports) => {
    return reports?.sort((a, b) => {
      const dateA = new Date(a.createdAt).valueOf()
      const dateB = new Date(b.createdAt).valueOf()
      return dateB - dateA
    }) || []
  }

  const handleDeleteSuccess = (deviceId: string, s3FileName: string) => {
    setIsTableUpdating(true)

    setTimeout(() => {
      setLocalReports((prev) =>
        prev.filter(
          (r) => !(r.deviceId === deviceId && r.s3FileName === s3FileName)
        )
      )
      setIsTableUpdating(false)
    }, 100)
  }

  const handleTableUpdate = () => {
    setIsTableUpdating(false)
  }

  const handleDeleteClick = (report) => {
    setReportToDelete({
      deviceId: report.deviceId,
      s3FileName: report.s3FileName
    })
    setDeleteModalOpen(true)
  }

  useEffect(() => {
    if (systemReports) {
      setLocalReports(systemReports)
    }
  }, [systemReports])

  useEffect(() => {
    if (!isDeleteModalOpen && reportToDelete === null) {
      setIsTableUpdating(true)
    }
  }, [isDeleteModalOpen, reportToDelete])

  return (
    <S.Panel id="link-system-reports" scrollToSection>
      <Panel.Header>
        <Panel.Title>System Reports
          <LockIcon featureCode="system_reports_history" featureComponentType={'title'} />
        </Panel.Title>
      </Panel.Header>

      {!areSystemReportsLoaded ? (
        <Spinner variant="page" />
      ) : (
        <>
          {isTableUpdating ? (
            <Spinner variant="page" />
          ) : (
            <Table
              items={
                (hasAccess('system_reports_history') && localReports) || []
              }
              noDataMessage={{
                title: showUpgrade('system_reports_history')
                  ? 'Upgrade to Premium'
                  : inFreeTrial('system_reports_history')
                    ? 'Diagnostics Premium Free-Trial'
                    : 'No Saved Reports',
                description: hoverTextMessage(
                  'system_reports_history',
                  'Reports will be displayed here if a system report is downloaded with the Save option selected.'
                )
              }}
            >
              <Table.Header>
                <Table.HeaderCell title="Name" />
                <Table.HeaderCell title="Created" />
                <Table.HeaderCell title="Actions" />
              </Table.Header>

              {SortByTimeStamp(localReports).map((report) => (
                <Table.Row key={report.s3FileName}>
                  <S.Cell>
                    <S.ReportFileLink
                      onClick={onClickReport}
                      href={report.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`View report: ${report.s3FileName}`}
                    >
                      <S.PdfIcon sx={{ fontSize: 40 }} />
                      <div>{report.s3FileName}</div>
                    </S.ReportFileLink>
                    <S.TrashIcon
                      className="mobile-trash"
                      onClick={() => handleDeleteClick(report)}
                    />
                  </S.Cell>
                  <S.Cell>
                    <S.ReportTimeStamp>
                      {shortDateTime(report.createdAt)}
                    </S.ReportTimeStamp>
                  </S.Cell>
                  <S.Cell>
                    <S.TrashIcon
                      className="desktop-trash"
                      onClick={() => handleDeleteClick(report)}
                    />
                  </S.Cell>
                </Table.Row>
              ))}
            </Table>
          )}
        </>
      )}
      <DeleteReportModal
        isDisplayed={isDeleteModalOpen}
        isDeleting={isDeleting}
        setIsDeleting={setIsDeleting}
        setDeleteModalOpen={setDeleteModalOpen}
        setReportToDelete={setReportToDelete}
        reportToDelete={reportToDelete}
        onDeleteSuccess={handleDeleteSuccess}
        onTableUpdate={handleTableUpdate}
      />
    </S.Panel>
  )
}

export default SystemReportsPanel

const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
    background-color: ${color('paneBackground')};
  `,
  ReportFileLink: styled.a`
    margin: 15px 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    overflow: hidden;
    div {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    @media ${screenSize.small} {
      margin: 0;
    }
  `,
  ReportTimeStamp: styled.div`
    margin: 15px 0;
    display: flex;

    @media ${screenSize.small} {
      margin: 0 0 0 35px;
    }
  `,
  Cell: styled(Table.Cell)`
    padding-left: 15px;

    @media ${screenSize.small} {
      padding-left: 0;
    }
  `,
  PdfIcon: styled(PictureAsPdfOutlined)`
    margin-right: 10px;
    font-size: 24px;
  `,
  TrashIcon: styled(DeleteOutlined)`
    margin: 15px;
    color: ${color('danger')};
    cursor: pointer;
    flex-shrink: 0;
    &:hover {
      color: ${color('errorMessage')};
    }
    &.mobile-trash {
      display: none;
      @media ${screenSize.small} {
        display: inline-flex;
        color: ${color('danger')};
        cursor: pointer;
        flex-shrink: 0;
        margin-left: 0.75rem;
      }
    }
    &.desktop-trash {
      display: inline-flex;
      color: ${color('danger')};
      cursor: pointer;
      @media ${screenSize.small} {
        display: none;
      }
    }
  `
}
